import { useEffect, useMemo } from "react"
import { AppHeader } from "@/components/common/app-sidebar/app-header"
import { AppSidebar } from "@/components/common/app-sidebar/app-sidebar"
import { NotificationsToastListener } from "@/components/common/web-socket/notifications-toast-listener"
import { SoundAlertListener } from "@/components/common/web-socket/sound-alert-listener"
import WhatsappSupport from "@/components/common/whatsapp-support"
import { AdminSidebar } from "@/features/admin/components"
import { CrmScopeBanner } from "@/features/auth/components/crm-scope-banner"
import { useIsCrmScoped } from "@/features/auth/hooks/use-organization-access"
import { useAuth } from "@/features/auth/hooks/useAuth"
import { SubscriptionGuardProvider } from "@/features/billing/components/subscription-guard-provider"
import { useBillingStore } from "@/features/billing/store/billing-store"
import { useSidebar } from "@/features/sidebar/hooks/useSidebar"
// import { initChatwoot } from "@/lib/support/chatwoot"
import { SidebarInset, SidebarProvider } from "@ui/sidebar"
import * as Ably from "ably"
import { AblyProvider, ChannelProvider } from "ably/react"
import { Outlet, useLocation, useNavigate } from "react-router-dom"
import { Toaster } from "sonner"

const baseURL = import.meta.env.VITE_API_URL || "https://api.speeke.ai/api/v1"

export default function MainLayout() {
  const { isOpen, setOpen } = useSidebar()
  const { getUserInfo } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const { user, token } = useAuth()
  const { fetchSubscriptionStatus } = useBillingStore()
  const isCrmScoped = useIsCrmScoped()
  const channelName = `private-org-${user?.organization_id}-messages`

  // Detectar se estamos na área admin
  const isAdminArea = location.pathname.startsWith("/admin")

  // Na área de CRM o conteúdo (board Kanban) precisa ficar limitado à viewport
  // para que o scroll horizontal/vertical funcione dentro do próprio board.
  const isCrmArea = location.pathname.startsWith("/leads")

  // CRM-scoped tokens are rejected on the websocket and every non-CRM route,
  // so keep the realtime client disconnected and skip billing bootstrap.
  const ablyClient = useMemo(
    () =>
      new Ably.Realtime({
        authUrl: `${baseURL}/communication/ably/auth`,
        authHeaders: {
          Authorization: `Bearer ${token}`,
        },
        autoConnect: !isCrmScoped,
      }),
    [token, isCrmScoped]
  )

  useEffect(() => {
    if (isCrmScoped) return
    const fetchSubscriptionStatusEffect = async () => {
      await fetchSubscriptionStatus()
    }
    fetchSubscriptionStatusEffect()
  }, [fetchSubscriptionStatus, isCrmScoped])

  useEffect(() => {
    // initChatwoot()
    const fetchUserInfo = async () => {
      const user = await getUserInfo()
      if (!user.onboarding_completed && user.active_organization_scope !== "crm") {
        navigate("/onboarding")
      }
    }

    fetchUserInfo()
  }, [])

  // Route guard for CRM-scoped access - every other area is denied server-side
  useEffect(() => {
    if (!isCrmScoped) return
    if (!location.pathname.startsWith("/leads")) {
      navigate("/leads", { replace: true })
    }
  }, [isCrmScoped, location.pathname, navigate])

  // Route guard for STAFF role - redirect to /chat if accessing restricted routes
  useEffect(() => {
    const isStaffOnly = user?.roles?.includes("staff") && !user?.roles?.includes("client")
    if (!isStaffOnly) return

    const allowedPaths = ["/chat", "/calendar", "/availability", "/contacts"]
    const isAllowed = allowedPaths.some(p => location.pathname.startsWith(p))
    if (!isAllowed) {
      navigate("/chat", { replace: true })
    }
  }, [location.pathname, user?.roles, navigate])

  return (
    <AblyProvider client={ablyClient}>
      <ChannelProvider channelName={channelName}>
        <SubscriptionGuardProvider>
          <SidebarProvider defaultOpen={isOpen} open={isOpen} onOpenChange={setOpen}>
            {isAdminArea ? <AdminSidebar /> : <AppSidebar />}
            {/* CRM needs a viewport-bounded height so the Kanban board scrolls
                internally instead of stretching the page. */}
            <SidebarInset className={isCrmArea ? "h-svh md:h-[calc(100svh-1rem)]" : undefined}>
              <CrmScopeBanner />
              <AppHeader />
              <div
                className={`flex min-w-0 flex-1 flex-col overflow-x-hidden ${
                  isCrmArea ? "min-h-0" : ""
                }`}
              >
                <div
                  className={`@container/main flex flex-1 flex-col gap-2 ${
                    isCrmArea ? "min-h-0" : ""
                  }`}
                >
                  <div
                    className={
                      isCrmArea
                        ? "flex min-h-0 flex-1 flex-col px-4 py-2 md:px-6 md:py-6"
                        : "flex flex-col gap-4 px-4 py-2 md:gap-6 md:px-6 md:py-6"
                    }
                  >
                    <Outlet />
                  </div>
                </div>
              </div>
            </SidebarInset>

            <WhatsappSupport />

            {!isCrmScoped && <NotificationsToastListener />}
            {!isCrmScoped && <SoundAlertListener />}
            <Toaster richColors />
          </SidebarProvider>
        </SubscriptionGuardProvider>
      </ChannelProvider>
    </AblyProvider>
  )
}
